import Firebase from "#classes/Firebase"
import {
  EVAL_COMMENT_TYPE,
  EVAL_EVENT_TYPE,
  type EvalCommentConstructor,
  type EvalEventType,
} from "#customTypes/composedTypes/evalCommentTypes"
import { EVALUATION_STATUS } from "#customTypes/composedTypes/evaluationTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { Uuid } from "#customTypes/globalTypes"
import { DatabaseObject } from "#customTypes/types"

const EVENT_LABELS: Record<EvalEventType, string> = {
  submitted: "Submitted",
  savedDraft: "Saved draft",
  approved: "Approved",
  rejected: "Rejected",
  printed: "Printed",
  authorAssigned: "",
  approverAssigned: "",
}

export const getEventLabel = (eventType: EvalEventType): string =>
  EVENT_LABELS[eventType] ?? eventType

/**
 * If the actor is the author (assignedTo), notify the approver.
 * Otherwise, notify the author.
 */
export const getEvalNotificationRecipient = (
  actorId: string,
  assignedTo?: string,
  approverId?: string
): string | null => {
  const recipientId =
    actorId === assignedTo ? (approverId ?? null) : (assignedTo ?? null)
  if (recipientId === actorId) return null
  return recipientId
}

export const markEvaluationPrinted = (
  evaluationId: Uuid,
  userId: Uuid | null | undefined
): Promise<void> =>
  Firebase.getInstance().updateEvaluationWithEvent({
    evaluationId,
    patch: {
      status: EVALUATION_STATUS.PRINTED,
      printedBy: userId,
      printedAt: new Date().toISOString(),
    },
    event: userId
      ? { authorId: userId, eventType: EVAL_EVENT_TYPE.PRINTED }
      : undefined,
  })

export const createEvalEvent = async (
  evaluationId: Uuid,
  authorId: Uuid,
  eventType: EvalEventType,
  message?: string
): Promise<void> => {
  const now = new Date().toISOString()

  await Firebase.getInstance().addDocument<
    EvalCommentConstructor & DatabaseObject
  >(DOC_TYPES.EVAL_COMMENTS, {
    evaluationId,
    authorId,
    message: message ?? "",
    type: EVAL_COMMENT_TYPE.EVENT,
    eventType,
    createdAt: now,
    createdBy: authorId,
    lastUpdatedAt: now,
    lastUpdatedBy: authorId,
  } as Omit<EvalCommentConstructor & DatabaseObject, "id">)
}
